import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Send, Eye, Mail, MessageCircle, CheckCircle2, XCircle, BellRing } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { errorText } from '../../lib/errors';
import { PageHeader, Card, CardHeader, CardBody, Field, Input, Select, Textarea, Button, Switch, Badge, DataTable, EmptyState, LoadingBlock, Modal } from '../../ui';

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const PLACEHOLDERS = ['{company}', '{amount}', '{date}', '{days}', '{days_overdue}', '{plan}', '{cycle}', '{code}'];

/** «7, 3, 1, 0» ⇄ [7, 3, 1, 0] */
const toText = (list) => (list || []).join(', ');
const toList = (text) => [...new Set(String(text).split(/[,\s]+/).filter(Boolean).map(Number).filter((n) => Number.isInteger(n) && n >= 0))];

/** Напоминания об абонплате: владельцу в Telegram и клиентам на почту */
export default function BillingRemindersPage() {
  const { t, fmt } = useI18n();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testTenant, setTestTenant] = useState('');

  const load = useCallback(async () => {
    try {
      const [res, logRes] = await Promise.all([api.get('/admin/billing/reminders'), api.get('/admin/billing/reminders/log?limit=50')]);
      setData(res.data);
      const s = res.data.settings;
      setForm({
        hour: s.hour,
        owner: { ...s.owner, days_before: toText(s.owner.days_before) },
        customer: { ...s.customer, days_before: toText(s.customer.days_before), overdue_days: toText(s.customer.overdue_days) },
      });
      setLog(logRes.data.items || []);
      if (!testTenant && res.data.companies[0]) setTestTenant(String(res.data.companies[0].tenant_id));
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    }
  }, [t, testTenant]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !form) return <LoadingBlock />;

  const setOwner = (patch) => setForm({ ...form, owner: { ...form.owner, ...patch } });
  const setCustomer = (patch) => setForm({ ...form, customer: { ...form.customer, ...patch } });

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await api.put('/admin/billing/reminders', {
        hour: Number(form.hour),
        owner: { ...form.owner, days_before: toList(form.owner.days_before) },
        customer: { ...form.customer, days_before: toList(form.customer.days_before), overdue_days: toList(form.customer.overdue_days).filter((n) => n > 0) },
      });
      toast.success(t('common.saved'));
      load();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const action = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy('');
    }
  };

  const showPreview = () => action('preview', async () => {
    const res = await api.post('/admin/billing/reminders/preview');
    setPreview(res.data);
  });

  const runNow = () => action('run', async () => {
    const res = await api.post('/admin/billing/reminders/run');
    const r = res.data;
    toast.success(t('reminders.runDone', { owner: r.owner.companies, sent: r.customer.sent, failed: r.customer.failed }));
    setPreview(null);
    load();
  });

  const testTelegram = () => action('tg', async () => {
    await api.post('/admin/billing/reminders/test-telegram');
    toast.success(t('reminders.testSent'));
  });

  const testEmail = (overdue) => action(overdue ? 'mail-overdue' : 'mail', async () => {
    await api.post('/admin/billing/reminders/test-email', { tenant_id: Number(testTenant), to: testTo.trim(), overdue });
    toast.success(t('reminders.testSent'));
  });

  const toggleCompany = (c) => action(`c${c.tenant_id}`, async () => {
    await api.put(`/admin/tenants/${c.tenant_id}`, { billing_reminders: !c.reminders_enabled });
    load();
  });

  const whenText = (days) => {
    if (days === 0) return t('reminders.today');
    if (days > 0) return t('reminders.inDays', { days });
    return t('reminders.overdueDaysLabel', { days: -days });
  };

  const companyColumns = [
    { key: 'name', header: t('companies.company'), render: (c) => <Link className="font-medium text-zinc-900 hover:underline dark:text-zinc-100" to={`/owner/companies/${c.tenant_id}?tab=billing`}>{c.name}</Link> },
    { key: 'due_date', header: t('billing.nextDate'), render: (c) => <span className="tabular">{fmt.date(c.due_date)}</span> },
    { key: 'days', header: t('reminders.when'), render: (c) => <Badge tone={c.days < 0 ? 'danger' : c.days <= 7 ? 'warning' : 'neutral'}>{whenText(c.days)}</Badge> },
    { key: 'amount', header: t('billing.amount'), align: 'right', render: (c) => <span className="tabular">{fmt.money(c.amount, c.currency)}</span> },
    { key: 'email', header: t('reminders.recipient'), hideOnMobile: true, render: (c) => c.email || <span className="text-zinc-400">{t('reminders.noEmail')}</span> },
    {
      key: 'reminders',
      header: t('reminders.emailsOn'),
      align: 'right',
      render: (c) => <Switch checked={c.reminders_enabled} onChange={() => toggleCompany(c)} disabled={busy === `c${c.tenant_id}`} label={t('reminders.emailsOn')} />,
    },
  ];

  const logColumns = [
    { key: 'created_at', header: t('reminders.sentAt'), render: (l) => <span className="tabular">{fmt.dateTime(l.created_at)}</span> },
    { key: 'company_name', header: t('companies.company'), render: (l) => l.company_name || '—' },
    { key: 'channel', header: t('reminders.channel'), render: (l) => (l.channel === 'email' ? <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{l.recipient}</span> : <span className="inline-flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" />Telegram</span>) },
    { key: 'days', header: t('reminders.when'), render: (l) => whenText(l.days) },
    { key: 'status', header: t('common.status'), render: (l) => (l.status === 'sent' ? <Badge tone="success">{t('reminders.sent')}</Badge> : <span className="inline-flex flex-col items-start gap-0.5"><Badge tone="danger">{t('reminders.failed')}</Badge><span className="max-w-xs truncate text-xs text-zinc-500">{l.error}</span></span>) },
  ];

  const channel = (ok, label, hint) => (
    <div className="flex items-start gap-2 text-sm">
      {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />}
      <div>
        <p className="font-medium text-zinc-900 dark:text-zinc-100">{label}</p>
        {!ok && <p className="text-xs text-zinc-500">{hint}</p>}
      </div>
    </div>
  );

  return (
    <>
      <PageHeader
        title={t('reminders.title')}
        description={t('reminders.subtitle')}
        actions={
          <>
            <Link to="/owner/billing"><Button variant="secondary" icon={ArrowLeft}>{t('billing.title')}</Button></Link>
            <Button variant="secondary" icon={Eye} onClick={showPreview} loading={busy === 'preview'}>{t('reminders.preview')}</Button>
          </>
        }
      />

      <form onSubmit={save} className="space-y-6">
        <Card>
          <CardBody className="grid gap-4 sm:grid-cols-3">
            {channel(data.channels.telegram, t('reminders.channelTelegram'), t('reminders.channelTelegramHint'))}
            {channel(data.channels.email, t('reminders.channelEmail'), t('reminders.channelEmailHint'))}
            <Field label={t('reminders.hour')} hint={t('reminders.hourHint')}>
              <Select value={form.hour} onChange={(e) => setForm({ ...form, hour: Number(e.target.value) })}>
                {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
              </Select>
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('reminders.ownerTitle')}
            description={t('reminders.ownerHint')}
            actions={<Switch checked={form.owner.enabled} onChange={(v) => setOwner({ enabled: v })} label={t('reminders.ownerTitle')} />}
          />
          <CardBody className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('reminders.daysBefore')} hint={t('reminders.daysBeforeHint')}>
                <Input value={form.owner.days_before} onChange={(e) => setOwner({ days_before: e.target.value })} placeholder="7, 3, 1, 0" disabled={!form.owner.enabled} />
              </Field>
              <div className="flex items-center justify-between gap-4 rounded-lg border border-zinc-200 px-3 py-2.5 dark:border-zinc-700">
                <div>
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{t('reminders.overdueDaily')}</p>
                  <p className="text-xs text-zinc-500">{t('reminders.overdueDailyHint')}</p>
                </div>
                <Switch checked={form.owner.overdue_daily} onChange={(v) => setOwner({ overdue_daily: v })} disabled={!form.owner.enabled} label={t('reminders.overdueDaily')} />
              </div>
            </div>
            <Button variant="secondary" size="sm" icon={Send} onClick={testTelegram} loading={busy === 'tg'} disabled={!data.channels.telegram}>{t('reminders.testTelegram')}</Button>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('reminders.customerTitle')}
            description={t('reminders.customerHint')}
            actions={<Switch checked={form.customer.enabled} onChange={(v) => setCustomer({ enabled: v })} label={t('reminders.customerTitle')} />}
          />
          <CardBody className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('reminders.daysBefore')} hint={t('reminders.daysBeforeHint')}>
                <Input value={form.customer.days_before} onChange={(e) => setCustomer({ days_before: e.target.value })} placeholder="5, 1" />
              </Field>
              <Field label={t('reminders.overdueDays')} hint={t('reminders.overdueDaysHint')}>
                <Input value={form.customer.overdue_days} onChange={(e) => setCustomer({ overdue_days: e.target.value })} placeholder="1, 3, 7" />
              </Field>
            </div>
            <p className="text-xs text-zinc-500">
              {t('reminders.placeholders')}{' '}
              {PLACEHOLDERS.map((p) => <code key={p} className="mr-1 rounded bg-zinc-100 px-1 py-0.5 text-[11px] text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">{p}</code>)}
            </p>
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('reminders.beforeLetter')}</p>
                <Field label={t('reminders.subject')}>
                  <Input value={form.customer.subject} onChange={(e) => setCustomer({ subject: e.target.value })} maxLength={200} />
                </Field>
                <Field label={t('reminders.body')}>
                  <Textarea rows={9} value={form.customer.body} onChange={(e) => setCustomer({ body: e.target.value })} maxLength={5000} />
                </Field>
              </div>
              <div className="space-y-3">
                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('reminders.overdueLetter')}</p>
                <Field label={t('reminders.subject')}>
                  <Input value={form.customer.overdue_subject} onChange={(e) => setCustomer({ overdue_subject: e.target.value })} maxLength={200} />
                </Field>
                <Field label={t('reminders.body')}>
                  <Textarea rows={9} value={form.customer.overdue_body} onChange={(e) => setCustomer({ overdue_body: e.target.value })} maxLength={5000} />
                </Field>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-3 rounded-lg bg-zinc-50 p-3 dark:bg-zinc-800/50">
              <Field label={t('reminders.testCompany')} className="w-56">
                <Select value={testTenant} onChange={(e) => setTestTenant(e.target.value)}>
                  {data.companies.map((c) => <option key={c.tenant_id} value={c.tenant_id}>{c.name}</option>)}
                </Select>
              </Field>
              <Field label={t('reminders.testTo')} className="w-64">
                <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" />
              </Field>
              <Button variant="secondary" size="sm" icon={Mail} onClick={() => testEmail(false)} loading={busy === 'mail'} disabled={!data.channels.email || !testTo || !testTenant}>{t('reminders.testBefore')}</Button>
              <Button variant="secondary" size="sm" icon={Mail} onClick={() => testEmail(true)} loading={busy === 'mail-overdue'} disabled={!data.channels.email || !testTo || !testTenant}>{t('reminders.testOverdue')}</Button>
            </div>
          </CardBody>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" icon={Save} loading={saving}>{t('common.save')}</Button>
        </div>
      </form>

      <h2 className="mb-3 mt-8 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('reminders.companiesTitle')}</h2>
      <DataTable columns={companyColumns} rows={data.companies} empty={<EmptyState icon={BellRing} title={t('reminders.noCompanies')} text={t('reminders.noCompaniesHint')} />} />

      <h2 className="mb-3 mt-8 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('reminders.logTitle')}</h2>
      <DataTable columns={logColumns} rows={log} empty={<EmptyState icon={Send} title={t('reminders.logEmpty')} />} />

      <Modal
        open={!!preview}
        onClose={() => setPreview(null)}
        title={t('reminders.previewTitle')}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPreview(null)}>{t('common.close')}</Button>
            <Button icon={Send} onClick={runNow} loading={busy === 'run'} disabled={!preview || (!preview.owner.length && !preview.customer.length)}>{t('reminders.sendNow')}</Button>
          </>
        }
      >
        {preview && (
          <div className="space-y-5 text-sm">
            <div>
              <p className="mb-2 font-semibold text-zinc-900 dark:text-zinc-100">{t('reminders.previewOwner')}</p>
              {preview.owner.length
                ? <ul className="space-y-1">{preview.owner.map((c) => <li key={c.tenant_id}>• {c.name} — {fmt.money(c.amount, c.currency)}, {whenText(c.days)}</li>)}</ul>
                : <p className="text-zinc-500">{t('reminders.previewNone')}</p>}
            </div>
            <div>
              <p className="mb-2 font-semibold text-zinc-900 dark:text-zinc-100">{t('reminders.previewCustomers')}</p>
              {preview.customer.length
                ? preview.customer.map((c) => (
                  <div key={c.tenant_id} className="mb-3 rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                    <p className="text-xs text-zinc-500">{c.email} · {whenText(c.days)}</p>
                    <p className="mt-1 font-medium text-zinc-900 dark:text-zinc-100">{c.subject}</p>
                    <pre className="mt-2 whitespace-pre-wrap font-sans text-zinc-600 dark:text-zinc-400">{c.text}</pre>
                  </div>
                ))
                : <p className="text-zinc-500">{t('reminders.previewNone')}</p>}
            </div>
            <p className="text-xs text-zinc-500">{t('reminders.previewNote')}</p>
          </div>
        )}
      </Modal>
    </>
  );
}
